import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MainLayout from '../../components/layout/MainLayout';
import { ROUTES } from '../../utils/constants';
import {
  fetchTrainingCurriculum,
  fetchTrainingLesson,
  finishLesson,
  nextLessonStep,
  personalizeLesson,
  previousLessonStep,
  startLesson,
} from '../../services/trainingService';
import TrainingStepPanel from './components/TrainingStepPanel';
import {
  lessonAvailabilityMessage,
  shouldShowLessonLimitations,
  shouldShowUnavailableWarning,
  trainingRoleLabel,
} from '../../utils/trainingAccess';
import TrainingMarkdown from './components/TrainingMarkdown';
import ReadAloudButton, { ReadAloudTextButton } from './components/ReadAloudButton';
import { stopSpeaking } from '../../utils/readAloud';

function findModuleName(curriculum, procedureId) {
  for (const module of curriculum?.modules || []) {
    for (const lesson of module.lessons || []) {
      if (lesson.procedureId === procedureId) {
        return module.name;
      }
    }
  }
  return '';
}

function findModuleId(curriculum, procedureId) {
  for (const module of curriculum?.modules || []) {
    for (const lesson of module.lessons || []) {
      if (lesson.procedureId === procedureId) {
        return module.id;
      }
    }
  }
  return '';
}

/** Strip internal status classification sentences for learner-facing intro. */
export function learnerFacingIntro(text) {
  return String(text || '')
    .replace(/\s*Status(?:\s+for[^:]*)?:\s*\*\*[^*]+\*\*[^.]*\.?/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function LessonPage() {
  const { procedureId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stepError, setStepError] = useState('');
  const [lesson, setLesson] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [completion, setCompletion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [personalIntro, setPersonalIntro] = useState('');
  const [personalizing, setPersonalizing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setStepError('');
    try {
      const [lessonResp, curriculumResp] = await Promise.all([
        fetchTrainingLesson(procedureId),
        fetchTrainingCurriculum(),
      ]);
      const lessonData = lessonResp?.data || lessonResp;
      const curriculumData = curriculumResp?.data || curriculumResp;
      if (!lessonData?.procedureId) {
        throw new Error('Lesson not found');
      }
      setLesson(lessonData);
      setCurriculum(curriculumData);

      const resume = curriculumData?.resume;
      const lessonProgress = (curriculumData?.modules || [])
        .flatMap((m) => m.lessons || [])
        .find((l) => l.procedureId === lessonData.procedureId)?.progress;

      if (lessonProgress?.status === 'completed') {
        setCompleted(true);
        setCurrentStep(lessonData.totalSteps || 1);
      } else if (lessonProgress?.currentStep) {
        setCurrentStep(Math.max(1, Number(lessonProgress.currentStep) || 1));
        if (lessonProgress.status !== 'in_progress') {
          try {
            await startLesson(lessonData.procedureId);
          } catch (startErr) {
            console.warn('[Training] startLesson failed', startErr);
          }
        }
      } else if (
        resume?.procedureId === lessonData.procedureId &&
        resume?.currentStep
      ) {
        setCurrentStep(Math.max(1, Number(resume.currentStep) || 1));
      } else {
        setCurrentStep(1);
        try {
          await startLesson(lessonData.procedureId);
        } catch (startErr) {
          // Lesson content still loads; progress can catch up on Next.
          console.warn('[Training] startLesson failed', startErr);
        }
      }
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setError(apiMessage || err?.message || 'Failed to load lesson');
    } finally {
      setLoading(false);
    }
  }, [procedureId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => () => stopSpeaking(), []);

  useEffect(() => {
    stopSpeaking();
  }, [currentStep, showAll, completed, procedureId]);

  const steps = lesson?.steps || [];
  const totalSteps = steps.length || lesson?.totalSteps || 0;
  const activeStep = useMemo(
    () => steps.find((s) => s.number === currentStep) || steps[currentStep - 1],
    [steps, currentStep]
  );
  const moduleName = findModuleName(curriculum, lesson?.procedureId);
  const moduleId = findModuleId(curriculum, lesson?.procedureId);
  const showUnavailable = shouldShowUnavailableWarning(lesson);
  const showLessonLimitations = shouldShowLessonLimitations(lesson);
  const availabilityMessage = lessonAvailabilityMessage(lesson);
  const liveOnly = Boolean(lesson?.liveValidationOnly);
  const aboutText = learnerFacingIntro(lesson?.whatThisAccomplishes);
  const aboutSpeakText = personalIntro || aboutText;
  const activeCaveat = activeStep?.caveat || '';
  const stepSpeakText = activeStep
    ? [`Step ${activeStep.number}.`, activeStep.text, activeCaveat].filter(Boolean).join(' ')
    : '';

  const handlePersonalize = useCallback(async () => {
    if (!lesson?.procedureId || personalizing) return;
    setPersonalizing(true);
    try {
      const resp = await personalizeLesson(lesson.procedureId);
      const intro = resp?.data?.intro ?? resp?.intro;
      if (intro) setPersonalIntro(intro);
    } catch {
      // optional — lesson still works without personalization
    } finally {
      setPersonalizing(false);
    }
  }, [lesson?.procedureId, personalizing]);

  const persistStep = useCallback(async (action) => {
    if (!lesson?.procedureId || busy) return;
    setBusy(true);
    setStepError('');
    try {
      const resp = await action(lesson.procedureId);
      const data = resp?.data || resp;
      const entry = data?.lessons?.[lesson.procedureId];
      if (entry?.currentStep != null) {
        setCurrentStep(Math.max(1, Number(entry.currentStep) || 1));
      }
      if (data?.completion) {
        setCompletion(data.completion);
        setCompleted(true);
      }
    } catch (err) {
      const apiMessage = err?.response?.data?.message || err?.message;
      setStepError(
        apiMessage
          ? `Couldn't save your training progress. Try again. (${apiMessage})`
          : "Couldn't save your training progress. Try again."
      );
    } finally {
      setBusy(false);
    }
  }, [busy, lesson?.procedureId]);

  const handleNext = () => persistStep(nextLessonStep);
  const handlePrevious = () => persistStep(previousLessonStep);
  const handleFinish = () => persistStep(finishLesson);

  if (loading) {
    return (
      <MainLayout scrollMain>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (error || !lesson) {
    return (
      <MainLayout scrollMain>
        <Box sx={{ p: 3 }}>
          <Typography color="error">{error || 'Lesson not found'}</Typography>
          <Button sx={{ mt: 2 }} component={RouterLink} to={ROUTES.TRAINING}>
            Back to Training
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout scrollMain>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(ROUTES.TRAINING)}
          sx={{ mb: 2 }}
        >
          Back to Training
        </Button>

        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              {moduleName || trainingRoleLabel(curriculum?.rolePath)}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {lesson.title}
            </Typography>
            {!completed && !showAll ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Step {currentStep} of {totalSteps}
              </Typography>
            ) : null}
          </Box>

          {showUnavailable ? (
            <Alert severity="warning" data-testid="lesson-unavailable-warning">
              <Typography variant="subtitle2" gutterBottom>
                Current availability
              </Typography>
              {availabilityMessage}
            </Alert>
          ) : null}

          {!completed ? (
            <Paper sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  About this lesson
                </Typography>
                <ReadAloudButton text={aboutSpeakText} label="Read about this lesson" />
              </Stack>
              <TrainingMarkdown readAloud={false}>{aboutText}</TrainingMarkdown>
              {personalIntro ? (
                <Box sx={{ mt: 2 }} data-testid="lesson-personalize">
                  <TrainingMarkdown readAloud={false}>{personalIntro}</TrainingMarkdown>
                </Box>
              ) : null}
              <Button
                size="small"
                sx={{ mt: 1 }}
                onClick={handlePersonalize}
                disabled={personalizing}
              >
                {personalizing ? 'Personalizing…' : 'Personalize this lesson'}
              </Button>
            </Paper>
          ) : null}

          {!completed && showLessonLimitations ? (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }} data-testid="lesson-limitations">
              <Typography variant="subtitle2" gutterBottom>
                Current availability
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {availabilityMessage || 'Some parts of this lesson may not be available in the current interface.'}
              </Typography>
            </Paper>
          ) : null}

          {stepError ? (
            <Alert severity="error" data-testid="lesson-step-error" onClose={() => setStepError('')}>
              {stepError}
            </Alert>
          ) : null}

          {!completed && showAll ? (
            <Paper sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">
                  All steps
                </Typography>
                <ReadAloudButton
                  text={steps.map((step) => `Step ${step.number}. ${step.text}`).join('\n\n')}
                  label="Read all steps aloud"
                />
              </Stack>
              <Stack spacing={2} divider={<Divider flexItem />}>
                {steps.map((step) => (
                  <Box key={step.id}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {step.subsection ? (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {step.subsection}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Step {step.number}
                        </Typography>
                        <TrainingMarkdown readAloud={false}>{step.text}</TrainingMarkdown>
                        {step.caveat ? (
                          <Alert severity="info" sx={{ mt: 1 }} data-testid={`step-caveat-${step.number}`}>
                            {step.caveat}
                          </Alert>
                        ) : null}
                      </Box>
                      <ReadAloudButton
                        text={[`Step ${step.number}.`, step.text, step.caveat].filter(Boolean).join(' ')}
                        label={`Read step ${step.number}`}
                      />
                    </Stack>
                  </Box>
                ))}
              </Stack>
              <Button sx={{ mt: 2 }} onClick={() => setShowAll(false)}>
                Return to step-by-step
              </Button>
            </Paper>
          ) : null}

          {!completed && !showAll && activeStep ? (
            <Paper sx={{ p: 2.5 }}>
              {activeStep.subsection ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {activeStep.subsection}
                </Typography>
              ) : null}
              <Typography variant="overline" color="primary.main">
                Step {activeStep.number} of {totalSteps}
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <TrainingMarkdown readAloud={false}>{activeStep.text}</TrainingMarkdown>
              </Box>
              {activeCaveat ? (
                <Alert severity="info" sx={{ mt: 1.5 }} data-testid="step-caveat">
                  {activeCaveat}
                </Alert>
              ) : null}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                If you&apos;re following along in Signal Bridge, perform this step when ready. You can
                also read through without making changes.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  disabled={currentStep <= 1 || busy}
                  onClick={handlePrevious}
                  data-testid="lesson-previous"
                >
                  Previous
                </Button>
                {currentStep < totalSteps ? (
                  <Button
                    variant="contained"
                    disabled={busy}
                    onClick={handleNext}
                    data-testid="lesson-next"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    disabled={busy}
                    onClick={handleFinish}
                    data-testid="lesson-finish"
                  >
                    Finish lesson
                  </Button>
                )}
                <ReadAloudTextButton
                  text={stepSpeakText}
                  data-testid="lesson-listen"
                >
                  Listen
                </ReadAloudTextButton>
                <Button variant="text" onClick={() => setShowAll(true)} data-testid="lesson-show-all">
                  Show all steps
                </Button>
                <Button variant="text" onClick={() => setAskOpen((v) => !v)} data-testid="lesson-ask-step">
                  Ask about this step
                </Button>
              </Stack>
              <TrainingStepPanel
                open={askOpen}
                onClose={() => setAskOpen(false)}
                lesson={lesson}
                moduleId={moduleId}
                rolePath={curriculum?.rolePath}
                currentStep={currentStep}
                activeStep={activeStep}
              />
            </Paper>
          ) : null}

          {completed ? (
            <Paper sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">
                  Lesson complete
                </Typography>
                <ReadAloudButton
                  text={[
                    'How to test it.',
                    lesson.howToTest,
                    liveOnly
                      ? 'Final confirmation requires testing on the physical device or live service.'
                      : '',
                    'What success looks like.',
                    lesson.success,
                    'Common mistakes.',
                    lesson.commonMistakes,
                  ].filter(Boolean).join('\n\n')}
                  label="Read completion notes aloud"
                />
              </Stack>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">How to test it</Typography>
                    <ReadAloudButton text={lesson.howToTest} label="Read how to test" />
                  </Stack>
                  <TrainingMarkdown readAloud={false}>{lesson.howToTest}</TrainingMarkdown>
                  {liveOnly ? (
                    <Alert severity="info" sx={{ mt: 1 }} data-testid="lesson-live-validation-note">
                      Final confirmation requires testing on the physical device or live service.
                    </Alert>
                  ) : null}
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">What success looks like</Typography>
                    <ReadAloudButton text={lesson.success} label="Read success criteria" />
                  </Stack>
                  <TrainingMarkdown readAloud={false}>{lesson.success}</TrainingMarkdown>
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">Common mistakes</Typography>
                    <ReadAloudButton text={lesson.commonMistakes} label="Read common mistakes" />
                  </Stack>
                  <TrainingMarkdown readAloud={false}>{lesson.commonMistakes}</TrainingMarkdown>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                <Button component={RouterLink} to={ROUTES.TRAINING}>
                  Return to Training
                </Button>
                {completion?.nextRecommendedLessonId ? (
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to={`${ROUTES.TRAINING}/lesson/${completion.nextRecommendedLessonId}`}
                  >
                    {completion?.onDemandRemaining?.length
                      ? 'Continue to next lesson'
                      : 'Next recommended lesson'}
                  </Button>
                ) : null}
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </Box>
    </MainLayout>
  );
}
